import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { RoleService } from '../entities/role.service';
import { SessionService } from './session.service';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private roleService = inject(RoleService);
    private sessionService = inject(SessionService);

    // Computed signal que deriva los permisos del usuario actual.
    // userId proviene de una signal reactiva: los permisos se recalculan
    // automáticamente al cambiar de usuario (login/logout).
    public myPermissions = computed(() => {
        const userId = this.sessionService.userId();
        const allRoles = this.roleService.rolesSignal();

        if (!userId || allRoles.length === 0) {
            // Si no hay usuario o roles, retornar array vacío (sin permisos)
            return [];
        }

        // Buscar todos los roles que contienen al usuario
        const myRoles = allRoles.filter(role => role.users && role.users.includes(userId));

        // Combinar todos los permisos de todos los roles del usuario (sin duplicados)
        const allPermissions = new Set<string>();
        myRoles.forEach(role => {
            role.permissions.forEach(perm => allPermissions.add(perm));
        });

        return Array.from(allPermissions);
    });

    // Expose loading state from RoleService
    public isLoading = this.roleService.isLoadingSignal;

    constructor() {
        // Al cambiar de usuario (login/logout), recargar los roles para que
        // myPermissions se recalcule con los datos del usuario correcto.
        effect(() => {
            const userId = this.sessionService.userId();
            if (userId) {
                this.roleService.loadRoles().subscribe({
                    error: () => { /* los permisos quedarán vacíos hasta el próximo refresh */ }
                });
            }
        });
    }

    /**
     * Verifica si el usuario actual tiene un permiso específico
     */
    hasPermission(permission: string): boolean {
        return this.myPermissions().includes(permission);
    }

    /**
     * Verifica si el usuario tiene al menos uno de los permisos especificados
     */
    hasAnyPermission(permissions: string[]): boolean {
        const myPerms = this.myPermissions();
        return permissions.some(perm => myPerms.includes(perm));
    }

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     */
    hasAllPermissions(permissions: string[]): boolean {
        const myPerms = this.myPermissions();
        return permissions.every(perm => myPerms.includes(perm));
    }
}
